/**
 * Reine Logik der Tabellen-Bausteine (Design-Guide `tabellen.md`): Sortierzustand, Seitenfolge,
 * Datums- und Zahlenformat. Kein React, keine Daten außer den Argumenten.
 */

export type SortRichtung = "ascending" | "descending";
export interface Sortierung {
  spalte: string;
  richtung: SortRichtung;
}
export type AriaSortWert = "ascending" | "descending" | "none";

/** `aria-sort` am `<th>`: genau eine Spalte sortiert, alle anderen sortierbaren „none“. */
export function ariaSortVon(spalteId: string, sortierung: Sortierung | undefined): AriaSortWert {
  return sortierung && sortierung.spalte === spalteId ? sortierung.richtung : "none";
}

/** Klick auf den Sortierknopf: andere Spalte → aufsteigend; dieselbe Spalte → Richtung wechseln. */
export function naechsteSortierung(aktuell: Sortierung | undefined, spalteId: string): Sortierung {
  if (aktuell && aktuell.spalte === spalteId) {
    return { spalte: spalteId, richtung: aktuell.richtung === "ascending" ? "descending" : "ascending" };
  }
  return { spalte: spalteId, richtung: "ascending" };
}

/** Satz für die Live-Region (Guide, Barrierefreiheit): „Sortiert nach Fällig, aufsteigend“. */
export function sortAnsage(kopf: string, richtung: SortRichtung): string {
  return `Sortiert nach ${kopf}, ${richtung === "ascending" ? "aufsteigend" : "absteigend"}`;
}

/** Seitenfolge für „Seiten“-Navigation: höchstens sieben Einträge, „…“ als Lücke. */
export function seitenFolge(aktuell: number, gesamt: number): Array<number | "luecke"> {
  if (gesamt <= 7) return Array.from({ length: Math.max(gesamt, 0) }, (_, i) => i + 1);
  const a = Math.min(Math.max(aktuell, 1), gesamt);
  if (a <= 4) return [1, 2, 3, 4, 5, "luecke", gesamt];
  if (a >= gesamt - 3) return [1, "luecke", gesamt - 4, gesamt - 3, gesamt - 2, gesamt - 1, gesamt];
  return [1, "luecke", a - 1, a, a + 1, "luecke", gesamt];
}

export type DatumArt = "frist" | "ereignis";
export interface DatumAnzeige {
  /** Sichtbarer Text. */
  text: string;
  /** Vollwert für `title`. */
  voll: string;
  /** Für `<time datetime>`. */
  datetime: string;
}

const TZ = "Europe/Berlin";
const TAG = new Intl.DateTimeFormat("de-DE", { timeZone: TZ, day: "2-digit", month: "2-digit", year: "numeric" });
const TAG_ZEIT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const TAG_TEILE = new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" });

function tagNummer(d: Date): number {
  const [j, m, t] = TAG_TEILE.format(d).split("-").map(Number);
  return Math.floor(Date.UTC(j!, m! - 1, t!) / 86_400_000);
}

/**
 * T14: Fristen immer absolut „TT.MM.JJJJ“; Ereignisse bis 7 Tage zurück relativ („heute“, „gestern“,
 * „vor 5 Tagen“), danach absolut; Zukunft immer absolut. Zeitzone Europe/Berlin.
 */
export function formatDatum(wert: string | Date, art: DatumArt, jetzt: Date = new Date()): DatumAnzeige {
  const d = typeof wert === "string" ? new Date(wert) : wert;
  if (Number.isNaN(d.getTime())) return { text: "–", voll: "", datetime: "" };
  const absolut = TAG.format(d);
  const voll = TAG_ZEIT.format(d);
  const datetime = d.toISOString();
  if (art === "frist") return { text: absolut, voll, datetime };
  const diff = tagNummer(jetzt) - tagNummer(d);
  if (diff === 0) return { text: "heute", voll, datetime };
  if (diff === 1) return { text: "gestern", voll, datetime };
  if (diff >= 2 && diff <= 7) return { text: `vor ${diff} Tagen`, voll, datetime };
  return { text: absolut, voll, datetime };
}

const ZAHL_FORMATE = new Map<number, Intl.NumberFormat>();
function zahlFormat(nachkomma: number): Intl.NumberFormat {
  let f = ZAHL_FORMATE.get(nachkomma);
  if (!f) {
    f = new Intl.NumberFormat("de-DE", { minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma });
    ZAHL_FORMATE.set(nachkomma, f);
  }
  return f;
}

/** T8: `de-DE`, Tausenderpunkt, Minuszeichen U+2212, Einheit mit geschütztem Leerzeichen. */
export function formatZahl(wert: number, optionen: { nachkomma?: number; einheit?: string } = {}): string {
  const nk = optionen.nachkomma ?? 0;
  const betrag = zahlFormat(nk).format(Math.abs(wert));
  const nullBetrag = Number(betrag.replace(/\./g, "").replace(",", ".")) === 0;
  const text = wert < 0 && !nullBetrag ? `−${betrag}` : betrag;
  return optionen.einheit ? `${text} ${optionen.einheit}` : text;
}
